import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Loader2, Car, Save, FileDown, User, Eye } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { SignatureCanvas } from "@/components/SignatureCanvas";
import { exportVehicleAssignmentToPDF } from "@/lib/contractPdfExport";
import { saveDocumentToEmployeeFile, getDocumentCategory, getDocumentTypeName } from "@/lib/documentStorage";
import { PdfPreviewDialog } from "./PdfPreviewDialog";

interface Employee {
  id: string;
  name: string;
  position: string;
  address_street: string | null;
  address_zip: string | null;
  address_city: string | null;
}

interface Vehicle {
  id: string;
  name: string;
  manufacturer: string | null;
  license_plate: string | null;
}

interface CompanySettings {
  name: string | null;
  street: string | null;
  house_number: string | null;
  postal_code: string | null;
  city: string | null;
}

export const VehicleAssignmentForm = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [companySettings, setCompanySettings] = useState<CompanySettings | null>(null);
  const [showSignature, setShowSignature] = useState(false);
  const [previewDoc, setPreviewDoc] = useState<any>(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  
  
  const [formData, setFormData] = useState({
    selectedEmployeeId: "",
    personName: "",
    personAddressStreet: "",
    personAddressZip: "",
    personAddressCity: "",
    position: "",
    selectedVehicleId: "",
    vehicleManufacturer: "Citroën Berlingo",
    vehicleLicensePlate: "",
    documentDate: new Date().toISOString().split("T")[0],
    effectiveDate: new Date().toISOString().split("T")[0],
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [employeesRes, vehiclesRes, companyRes] = await Promise.all([
        supabase.from("employees").select("id, name, position, address_street, address_zip, address_city").is("archived_at", null),
        supabase.from("vehicles").select("id, name, manufacturer, license_plate"),
        supabase.from("company_settings").select("name, street, house_number, postal_code, city").single()
      ]);

      if (employeesRes.data) setEmployees(employeesRes.data);
      if (vehiclesRes.data) setVehicles(vehiclesRes.data);
      if (companyRes.data) setCompanySettings(companyRes.data);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleEmployeeSelect = (employeeId: string) => {
    const employee = employees.find(e => e.id === employeeId);
    if (employee) {
      setFormData(prev => ({
        ...prev,
        selectedEmployeeId: employeeId,
        personName: employee.name,
        personAddressStreet: employee.address_street || "",
        personAddressZip: employee.address_zip || "",
        personAddressCity: employee.address_city || "",
        position: employee.position || "",
      }));
    }
  };

  const handleVehicleSelect = (vehicleId: string) => {
    const vehicle = vehicles.find(v => v.id === vehicleId);
    if (vehicle) {
      setFormData(prev => ({
        ...prev,
        selectedVehicleId: vehicleId,
        vehicleManufacturer: vehicle.manufacturer || vehicle.name || "Citroën Berlingo",
        vehicleLicensePlate: vehicle.license_plate || "",
      }));
    }
  };

  const handleSave = async (signatureUrl?: string) => {
    if (!user) return;
    if (!formData.personName || !formData.vehicleManufacturer) {
      toast.error("Bitte füllen Sie alle Pflichtfelder aus");
      return;
    }

    setSaving(true);
    try {
      const { error } = await supabase.from("contract_templates").insert({
        template_type: "vehicle_assignment",
        employee_id: formData.selectedEmployeeId || null,
        person_name: formData.personName,
        person_address_street: formData.personAddressStreet,
        person_address_zip: formData.personAddressZip,
        person_address_city: formData.personAddressCity,
        position: formData.position,
        effective_date: formData.effectiveDate,
        custom_text: `${formData.vehicleManufacturer}|${formData.vehicleLicensePlate}`,
        signature_url: signatureUrl,
        signed_at: signatureUrl ? new Date().toISOString() : null,
        signed_by: signatureUrl ? formData.selectedEmployeeId : null,
        status: signatureUrl ? "signed" : "draft",
        created_by: user.id,
      });

      if (error) throw error;

      // Create + archive PDF to documents
      if (formData.selectedEmployeeId) {
        try {
          const pdfDoc = await exportVehicleAssignmentToPDF(
            {
              personName: formData.personName,
              personAddress: `${formData.personAddressStreet}, ${formData.personAddressZip} ${formData.personAddressCity}`,
              position: formData.position,
              vehicleManufacturer: formData.vehicleManufacturer,
              vehicleLicensePlate: formData.vehicleLicensePlate,
              documentDate: formData.documentDate,
              effectiveDate: formData.effectiveDate,
              companyName: companySettings?.name || "",
              companyAddress: companySettings
                ? `${companySettings.street || ""} ${companySettings.house_number || ""}, ${companySettings.postal_code || ""} ${companySettings.city || ""}`
                : "",
              signatureUrl,
            },
            false
          );

          const category = getDocumentCategory("vehicle_assignment");
          const typeName = getDocumentTypeName("vehicle_assignment");
          const result = await saveDocumentToEmployeeFile(
            formData.selectedEmployeeId,
            `${typeName} - ${formData.vehicleLicensePlate || formData.vehicleManufacturer}`,
            category,
            pdfDoc,
            user.id
          );
          if (result.success) {
            toast.success("Fahrzeugüberlassung gespeichert und in Dokumente abgelegt");
          } else {
            console.warn("Dokument konnte nicht gespeichert werden:", result.error);
            toast.error("Fahrzeugüberlassung gespeichert, PDF-Ablage fehlgeschlagen");
          }
        } catch (e) {
          console.error("Error archiving PDF:", e);
          toast.error("Fahrzeugüberlassung gespeichert, PDF-Ablage fehlgeschlagen");
        }
      } else {
        toast.success("Fahrzeugüberlassung wurde gespeichert");
      }
      setShowSignature(false);
      resetForm();
    } catch (error) {
      console.error("Error saving contract:", error);
      toast.error("Fehler beim Speichern");
    } finally {
      setSaving(false);
    }
  };

  const buildPdfPayload = () => ({
    personName: formData.personName,
    personAddress: `${formData.personAddressStreet}, ${formData.personAddressZip} ${formData.personAddressCity}`,
    position: formData.position,
    vehicleManufacturer: formData.vehicleManufacturer,
    vehicleLicensePlate: formData.vehicleLicensePlate,
    documentDate: formData.documentDate,
    effectiveDate: formData.effectiveDate,
    companyName: companySettings?.name || "",
    companyAddress: companySettings ? `${companySettings.street || ""} ${companySettings.house_number || ""}, ${companySettings.postal_code || ""} ${companySettings.city || ""}` : "",
  });

  const handlePreview = async () => {
    if (!formData.personName || !formData.vehicleManufacturer) {
      toast.error("Bitte füllen Sie Mitarbeiter und Fahrzeug aus");
      return;
    }
    try {
      const pdfDoc = await exportVehicleAssignmentToPDF(buildPdfPayload(), false);
      setPreviewDoc(pdfDoc);
      setPreviewOpen(true);
    } catch (e) {
      console.error("Error generating preview:", e);
      toast.error("Fehler beim Erstellen der Vorschau");
    }
  };

  const handleExportPDF = async () => {
    if (!formData.personName || !formData.vehicleManufacturer) {
      toast.error("Bitte füllen Sie alle Pflichtfelder aus");
      return;
    }

    try {
      const pdfDoc = await exportVehicleAssignmentToPDF({
        personName: formData.personName,
        personAddress: `${formData.personAddressStreet}, ${formData.personAddressZip} ${formData.personAddressCity}`,
        position: formData.position,
        vehicleManufacturer: formData.vehicleManufacturer,
        vehicleLicensePlate: formData.vehicleLicensePlate,
        documentDate: formData.documentDate,
        effectiveDate: formData.effectiveDate,
        companyName: companySettings?.name || "",
        companyAddress: companySettings ? `${companySettings.street || ""} ${companySettings.house_number || ""}, ${companySettings.postal_code || ""} ${companySettings.city || ""}` : "",
      }, false);

      // Save to employee documents if employee is selected
      if (formData.selectedEmployeeId && user) {
        const category = getDocumentCategory('vehicle_assignment');
        const typeName = getDocumentTypeName('vehicle_assignment');
        const result = await saveDocumentToEmployeeFile(
          formData.selectedEmployeeId,
          `${typeName} - ${formData.vehicleLicensePlate || formData.vehicleManufacturer}`,
          category,
          pdfDoc,
          user.id
        );
        if (result.success) {
          toast.success("PDF wurde erstellt und in den Mitarbeiter-Dokumenten gespeichert");
        } else {
          console.warn("Dokument konnte nicht gespeichert werden:", result.error);
        }
      }

      pdfDoc.save(`Fahrzeugueberlassung_${formData.personName.replace(/\s+/g, "_")}.pdf`);
      toast.success("PDF wurde erstellt");
    } catch (error) {
      console.error("Error exporting PDF:", error);
      toast.error("Fehler beim Erstellen des PDF");
    }
  };

  const resetForm = () => {
    setFormData({
      selectedEmployeeId: "",
      personName: "",
      personAddressStreet: "",
      personAddressZip: "",
      personAddressCity: "",
      position: "",
      selectedVehicleId: "",
      vehicleManufacturer: "Citroën Berlingo",
      vehicleLicensePlate: "",
      documentDate: new Date().toISOString().split("T")[0],
      effectiveDate: new Date().toISOString().split("T")[0],
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Car className="h-5 w-5" />
          Fahrzeugüberlassung erstellen
        </CardTitle>
        <CardDescription>
          Erstellen Sie eine Vereinbarung zur dienstlichen Fahrzeugüberlassung
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Employee Selection */}
        <div className="space-y-4 p-4 bg-muted/50 rounded-lg">
          <h4 className="font-medium">Mitarbeiter auswählen</h4>
          
          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <Label>Mitarbeiter auswählen</Label>
              <Select value={formData.selectedEmployeeId} onValueChange={handleEmployeeSelect}>
                <SelectTrigger>
                  <SelectValue placeholder="Mitarbeiter wählen..." />
                </SelectTrigger>
                <SelectContent>
                  {employees.map(emp => (
                    <SelectItem key={emp.id} value={emp.id}>{emp.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Name *</Label>
              <Input 
                value={formData.personName} 
                onChange={(e) => setFormData(prev => ({ ...prev, personName: e.target.value }))}
              />
            </div>
          </div>

          <div className="grid gap-4 md:grid-cols-3">
            <div>
              <Label>Straße</Label>
              <Input 
                value={formData.personAddressStreet} 
                onChange={(e) => setFormData(prev => ({ ...prev, personAddressStreet: e.target.value }))}
              />
            </div>
            <div>
              <Label>PLZ</Label>
              <Input 
                value={formData.personAddressZip} 
                onChange={(e) => setFormData(prev => ({ ...prev, personAddressZip: e.target.value }))}
              />
            </div>
            <div>
              <Label>Stadt</Label>
              <Input 
                value={formData.personAddressCity} 
                onChange={(e) => setFormData(prev => ({ ...prev, personAddressCity: e.target.value }))}
              />
            </div>
          </div>

          <div>
            <Label>Position</Label>
            <Input 
              value={formData.position} 
              onChange={(e) => setFormData(prev => ({ ...prev, position: e.target.value }))}
            />
          </div>
        </div>

        {/* Vehicle Details */}
        <div className="space-y-4 p-4 bg-muted/50 rounded-lg">
          <h4 className="font-medium">Fahrzeugdaten</h4>
          <div className="grid gap-4 md:grid-cols-2">
            {vehicles.length > 0 && (
              <div>
                <Label>Fahrzeug aus Bestand wählen (optional)</Label>
                <Select value={formData.selectedVehicleId} onValueChange={handleVehicleSelect}>
                  <SelectTrigger>
                    <SelectValue placeholder="Fahrzeug wählen..." />
                  </SelectTrigger>
                  <SelectContent>
                    {vehicles.map(vehicle => (
                      <SelectItem key={vehicle.id} value={vehicle.id}>
                        {vehicle.name} {vehicle.license_plate ? `(${vehicle.license_plate})` : ""}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div>
              <Label>Fahrzeugbezeichnung *</Label>
              <Input 
                value={formData.vehicleManufacturer} 
                onChange={(e) => setFormData(prev => ({ ...prev, vehicleManufacturer: e.target.value }))}
                placeholder="z.B. Citroën Berlingo"
              />
            </div>
          </div>

          <div className="grid gap-4 md:grid-cols-3">
            <div>
              <Label>Amtliches Kennzeichen</Label>
              <Input 
                value={formData.vehicleLicensePlate} 
                onChange={(e) => setFormData(prev => ({ ...prev, vehicleLicensePlate: e.target.value }))}
                placeholder="z.B. K-AB 1234"
              />
            </div>
            <div>
              <Label>Dokumentendatum</Label>
              <Input 
                type="date"
                value={formData.documentDate} 
                onChange={(e) => setFormData(prev => ({ ...prev, documentDate: e.target.value }))}
              />
            </div>
            <div>
              <Label>Gültig ab</Label>
              <Input 
                type="date"
                value={formData.effectiveDate} 
                onChange={(e) => setFormData(prev => ({ ...prev, effectiveDate: e.target.value }))}
              />
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex flex-col sm:flex-row gap-3 justify-end pt-4 border-t">
          <Button variant="outline" onClick={handlePreview} disabled={saving}>
            <Eye className="h-4 w-4 mr-2" />
            Vorschau
          </Button>
          <Button variant="outline" onClick={handleExportPDF} disabled={saving}>
            <FileDown className="h-4 w-4 mr-2" />
            PDF exportieren
          </Button>
          <Button variant="outline" onClick={() => setShowSignature(true)} disabled={saving}>
            <User className="h-4 w-4 mr-2" />
            Mit Unterschrift speichern
          </Button>
          <Button onClick={() => handleSave()} disabled={saving}>
            {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
            Als Entwurf speichern
          </Button>
        </div>

        {/* Signature Dialog */}
        {showSignature && (
          <div className="fixed inset-0 z-50 bg-background/80 flex items-center justify-center p-4">
            <Card className="w-full max-w-lg">
              <CardHeader>
                <CardTitle>Unterschrift des Arbeitnehmers</CardTitle>
                <CardDescription>
                  Der Arbeitnehmer bestätigt mit seiner Unterschrift die Vereinbarung
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <SignatureCanvas 
                  onSave={(signatureUrl) => handleSave(signatureUrl)}
                  title="Unterschrift"
                  description="Bitte unterschreiben Sie zur Bestätigung der Fahrzeugüberlassung"
                  confirmationText="Mit Ihrer Unterschrift bestätigen Sie die Vereinbarung zur Fahrzeugüberlassung."
                />
                <Button 
                  variant="outline" 
                  onClick={() => setShowSignature(false)}
                  className="w-full"
                >
                  Abbrechen
                </Button>
              </CardContent>
            </Card>
          </div>
        )}
      </CardContent>
      <PdfPreviewDialog
        open={previewOpen}
        onOpenChange={setPreviewOpen}
        pdfDoc={previewDoc}
        title="Vorschau: Fahrzeugüberlassung"
        fileName={`Fahrzeugueberlassung_${formData.personName.replace(/\s+/g, "_") || "Vorschau"}.pdf`}
      />
    </Card>
  );
};
